import React, { useCallback, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { App } from 'antd';
import type { InputNode } from '../types';
import { UploadChip } from './UploadChip';
import { parseStepsTooltip } from '../utils/stepsTooltip';
import { StepsTutorial } from './StepsTutorial';
import {
  isUploadFileTooLarge,
  shouldNotifyUploadSizeError,
  UPLOAD_FILE_SIZE_ERROR,
} from './uploadLimits';

/**
 * 测量批量 chips 容器是否换行（多行）。
 *
 * 背景：required 批量 upload 组是 inline-flex 盒，在编辑器 inline 流里默认
 * vertical-align:middle。单行时高 30px、与上方间距 6px（正常）；一旦 chips 换到
 * 第二行，整组高度涨到 64px+，middle 对齐让盒子顶部相对单行基准上探约 4px——
 * 实测上探量恒为 4px（与行数无关），表现为「第一排图片被往上挤、贴近上方数量行」。
 *
 * 修法：多行时给 group 补 margin-top:4px 抵消上探（见 index.css 的 .rte-batch-wrapped）。
 * 单行不能加（否则 6→8px 偏大），故必须运行时测量。V1 靠命令式 JS 切类，V2 用
 * ResizeObserver 干净实现：容器高度超单行阈值(32px)即判定换行。
 */
const SINGLE_ROW_MAX = 32;
const useIsWrapped = (ref: React.RefObject<HTMLElement | null>): boolean => {
  const [wrapped, setWrapped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWrapped(el.offsetHeight > SINGLE_ROW_MAX);
    measure();
    // jsdom（测试环境）及个别老浏览器无 ResizeObserver：降级为仅首测，不再监听 resize。
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });
  return wrapped;
};


type Props = {
  node: InputNode;
  files: Record<string, File | string | null>;
  onFileChange: (key: string, file: File | string | null) => void;
  disabled?: boolean;
  error?: string;
  /** 校验失败提示（图片超 10MB 等）。业务可接 antd message；不接则 console.warn。 */
  onValidationError?: (msg: string) => void;
};

const acceptFor = (node: InputNode): string | undefined => {
  if (node.accept) return node.accept;
  if (node.acceptType === 'image') return 'image/*';
  if (node.acceptType === 'file') return '.csv,.xlsx,.xls,.txt,.pdf,.doc,.docx,.json';
  return undefined;
};

const normalizeTooltip = (t: InputNode['tooltip']): string | undefined => {
  if (!t) return undefined;
  return typeof t === 'string' ? t : JSON.stringify(t);
};

const HINT_MIN_LEN = 12;

/**
 * Upload 节点 = chip 编排器。所有渲染单元委派给 UploadChip。
 *
 * 关键决策：
 * - 批量模式 slot 数动态：min(maxCount, max(minCount, filledCount + 1))，
 *   视觉与 V1 一致（已填 N 个时显示 N+1 个空 slot 引导用户继续上传）
 * - 多文件批量：input 自带 multiple，handler 从被点 slot 起线性扫空 slot 填
 * - 10MB 全类型校验：在 UploadNode 层统一过滤超限文件（不分图片/非图片），超限直接丢弃 + onValidationError（1 秒去重）
 * - hint：仅 node.showHint===true && hasFile && text>=12 时渲染
 */
export const UploadNode: React.FC<Props> = React.memo(
  ({ node, files, onFileChange, disabled, error, onValidationError }) => {
    const { message } = App.useApp();
    const isBatch =
      typeof node.maxCount === 'number' &&
      typeof node.minCount === 'number' &&
      node.maxCount > 1;

    const accept = acceptFor(node);
    // 批量 chips 容器 ref + 是否换行（多行时给 group 补 margin-top 抵消 inline 居中上探）
    const batchChipsRef = useRef<HTMLSpanElement>(null);
    const batchWrapped = useIsWrapped(batchChipsRef);
    // steps tooltip 走「查看教程」按钮：UploadChip 内不渲染 info-icon（传 undefined），
    // 由 UploadNode 在 chip 同级渲染 StepsTutorial。普通文案 tooltip 仍透传给 chip 的 info-icon。
    const stepsTooltip = parseStepsTooltip(node.tooltip);
    const isSteps = !!stepsTooltip;
    const tooltip = isSteps ? undefined : normalizeTooltip(node.tooltip);

    const reportError = useCallback(
      (msg: string) => {
        if (onValidationError) onValidationError(msg);
        else message.error(msg);
      },
      [onValidationError, message],
    );

    /** 过滤超 10MB 文件（全类型，对齐 V1），返回 accepted 数组。
     *  有文件被拒时发提示，但 1 秒内去重（批量/连续选超大文件只提示一次）。 */
    const validateAndFilter = useCallback(
      (input: FileList | File[]): File[] => {
        const list = Array.from(input);
        const accepted: File[] = [];
        let rejected = 0;
        for (const f of list) {
          if (isUploadFileTooLarge(f)) rejected += 1;
          else accepted.push(f);
        }
        if (rejected > 0 && shouldNotifyUploadSizeError()) {
          reportError(UPLOAD_FILE_SIZE_ERROR);
        }
        return accepted;
      },
      [reportError],
    );

    /** 单文件模式 onPick；同时用作「已填非图片 chip 重选替换本 slot」的回调
     *  （V1 editorClickHandler.ts:524-534：替换始终只填被点的那个 slot，单文件） */
    const handlePickSingle = useCallback(
      (slotKey: string, list: FileList) => {
        const accepted = validateAndFilter(list);
        if (accepted.length === 0) return;
        onFileChange(slotKey, accepted[0]);
      },
      [onFileChange, validateAndFilter],
    );

    /** 批量模式 onPick：从被点 slot 起线性扫空 slot 填 */
    const handlePickBatch = useCallback(
      (clickedSlotKey: string, list: FileList) => {
        const accepted = validateAndFilter(list);
        if (accepted.length === 0) return;

        const baseKey = node.key!;
        const maxCount = node.maxCount ?? 0;
        const clickedIdx = (() => {
          const m = clickedSlotKey.match(/-(\d+)$/);
          return m ? Number.parseInt(m[1], 10) : 0;
        })();

        let slot = Math.max(0, Math.min(clickedIdx, maxCount - 1));
        for (const f of accepted) {
          while (slot < maxCount && files[`${baseKey}-${slot}`] != null) slot += 1;
          if (slot >= maxCount) break;
          onFileChange(`${baseKey}-${slot}`, f);
          slot += 1;
        }
      },
      [files, node.key, node.maxCount, onFileChange, validateAndFilter],
    );

    /** 移除文件。
     *  批量模式走「紧凑补位」：删某 slot 后，把剩余已填 file 按 index 升序重排到
     *  0..n-1、尾部清空——不留稀疏空洞。否则删中间一张会腾出低 index 空位，下次在
     *  末尾空按钮上传的新图会填进那个低 index 空位、被升序渲染插到前面（用户反馈：
     *  「后面上传的图跑到了最前面」）。紧凑后视觉顺序恒等于 slot index 顺序。 */
    const handleRemove = useCallback(
      (slotKey: string) => {
        if (!isBatch) {
          onFileChange(slotKey, null);
          return;
        }
        const baseKey = node.key!;
        const max = node.maxCount ?? 0;
        const remaining: Array<File | string> = [];
        for (let i = 0; i < max; i += 1) {
          const k = `${baseKey}-${i}`;
          if (k === slotKey) continue;
          const f = files[k];
          if (f != null) remaining.push(f);
        }
        for (let i = 0; i < max; i += 1) {
          onFileChange(`${baseKey}-${i}`, i < remaining.length ? remaining[i] : null);
        }
      },
      [isBatch, files, node.key, node.maxCount, onFileChange],
    );

    /** 批量模式：分别收集已填 / 未填的 slotKey（按 slot index 顺序）。
     *  渲染时已填排前、未填排后 —— state 保持稀疏（slotKey ↔ File 引用稳定，
     *  useObjectURL 不重算、不闪烁），但视觉上始终连续。
     *  修复 bug：state 中间被清空（如删除 slot 0、1 后只剩 slot 2）时，
     *  原先按 index 0..renderCount-1 渲染会截断高 index 的已填 slot，
     *  导致已填 file 在 state 里残留但 UI 不可见、且会被 buildFormData 提交。 */
    const { filledEntries, emptyKeys } = useMemo(() => {
      if (!isBatch) {
        return { filledEntries: [] as Array<{ key: string; file: File | string }>, emptyKeys: [] as string[] };
      }
      const baseKey = node.key!;
      const filled: Array<{ key: string; file: File | string }> = [];
      const empty: string[] = [];
      for (let i = 0; i < (node.maxCount ?? 0); i += 1) {
        const k = `${baseKey}-${i}`;
        const f = files[k];
        if (f != null) filled.push({ key: k, file: f });
        else empty.push(k);
      }
      return { filledEntries: filled, emptyKeys: empty };
    }, [files, isBatch, node.key, node.maxCount]);
    const filledCount = filledEntries.length;

    /** 单文件模式 */
    if (!isBatch) {
      const slotKey = node.key!;
      const file = files[slotKey] ?? null;
      const hasFile = file != null;
      const hintText = (node.text || '').trim();
      const showHint =
        hasFile && node.showHint === true && hintText.length >= HINT_MIN_LEN;

      const chip = (
        <UploadChip
          slotKey={slotKey}
          file={file}
          text={node.text}
          accept={accept}
          tooltip={tooltip}
          disabled={disabled}
          exclusionGroup={node.exclusionGroup}
          onPick={handlePickSingle}
          onReplace={handlePickSingle}
          onRemove={handleRemove}
        />
      );

      const hintEl = showHint ? (
        <span
          className="exclusion-wrapper upload-hint"
          data-group={node.exclusionGroup}
        >
          {hintText}
        </span>
      ) : null;

      // steps tooltip：chip 同级「查看教程」按钮，已传文件时隐藏（V1 parity）
      const tutorialEl =
        isSteps && stepsTooltip ? (
          <StepsTutorial data={stepsTooltip} hidden={hasFile} />
        ) : null;

      if (node.required) {
        return (
          <span className={error ? 'rte-upload-error' : undefined}>
            <span
              className="exclusion-wrapper required-upload-group"
              data-group={node.exclusionGroup}
            >
              <span className="rte-text-required">
                <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
              </span>
              {chip}
            </span>
            {tutorialEl}
            {hintEl}
          </span>
        );
      }

      return (
        <span className={error ? 'rte-upload-error' : undefined}>
          {chip}
          {tutorialEl}
          {hintEl}
        </span>
      );
    }

    /** 批量模式：动态 slot 数 = min(max, max(min, filled + 1))
     *  渲染顺序 = [已填 chips（按 slot index 升序）] + [空 buttons（取首个未填 slot 起）] */
    const minCount = node.minCount ?? 1;
    const maxCount = node.maxCount ?? 0;
    const renderCount = Math.min(
      maxCount,
      Math.max(minCount, filledCount + (filledCount < maxCount ? 1 : 0)),
    );

    const slots: React.ReactElement[] = [];
    for (const { key: slotKey, file } of filledEntries) {
      slots.push(
        <UploadChip
          key={slotKey}
          slotKey={slotKey}
          file={file}
          text={node.text}
          accept={accept}
          tooltip={tooltip}
          disabled={disabled}
          batch
          multiple
          exclusionGroup={node.exclusionGroup}
          onPick={handlePickBatch}
          onReplace={handlePickSingle}
          onRemove={handleRemove}
        />,
      );
    }
    const emptyNeeded = Math.max(0, renderCount - filledCount);
    for (const slotKey of emptyKeys.slice(0, emptyNeeded)) {
      slots.push(
        <UploadChip
          key={slotKey}
          slotKey={slotKey}
          file={null}
          text={node.text}
          accept={accept}
          tooltip={tooltip}
          disabled={disabled}
          batch
          multiple
          exclusionGroup={node.exclusionGroup}
          onPick={handlePickBatch}
          onReplace={handlePickSingle}
          onRemove={handleRemove}
        />,
      );
    }

    if (node.required) {
      return (
        <span
          className={`exclusion-wrapper required-upload-group rte-required-upload-batch${batchWrapped ? ' rte-batch-wrapped' : ''}${error ? ' rte-upload-error' : ''}`}
          data-group={node.exclusionGroup}
        >
          <span className="rte-text-required">
            <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
          </span>
          <span className="rte-upload-batch-chips" ref={batchChipsRef}>{slots}</span>
          {isSteps && stepsTooltip ? (
            <StepsTutorial data={stepsTooltip} hidden={filledCount > 0} />
          ) : null}
        </span>
      );
    }

    return (
      <span
        className={`rte-upload-batch-chips${batchWrapped ? ' rte-batch-wrapped' : ''}${error ? ' rte-upload-error' : ''}`}
        ref={batchChipsRef}
      >
        {slots}
        {isSteps && stepsTooltip ? (
          <StepsTutorial data={stepsTooltip} hidden={filledCount > 0} />
        ) : null}
      </span>
    );
  },
);
UploadNode.displayName = 'UploadNode';
